import {
  canCancelSwapShift,
  canClaimOpenShift,
  canDeleteShift,
  canPublishShift,
  canReassignShift,
  canReplaceShift,
  canSwapShift,
  canUnassignShift,
  canWithdrawOpenShiftRequest,
} from '@/lib/actions/availableShiftActions';
import { shiftSwapPendingApproval } from '@/lib/schedule-v2/shiftV2Functions';
import {
  isAssigned,
  isLocked,
  isOpenAndUnassigned,
  isPublished,
  isUnassignedAndNotOpen,
} from '@/lib/schedule-v2/shiftV2Status';
import { isFuture } from '@/util/dateArithmetic';
import {
  OpenShiftResponse,
  ScheduleSettings,
  Shift,
  ShiftSwap,
} from '../../../api/v1';

type IsEnabled<T extends object = any> = (params: T) => boolean;

interface ShiftActionControlInterface {
  isEnabled: IsEnabled;
  isOpen: boolean;
  call: (shift: Shift) => void;
  close: (clearShift: boolean) => void;
}

export enum ShiftActionControlEnum {
  AssignShift = 'assignShift',
  CancelShiftSwap = 'cancelShiftSwap',
  ClaimOpenShift = 'claimOpenShift',
  DeleteShift = 'deleteShift',
  DuplicateShift = 'duplicateShift',
  EditShift = 'editShift',
  MoveToOpen = 'moveToOpen',
  MoveToUnassigned = 'moveToUnassigned',
  PublishShift = 'publishShift',
  ReassignShift = 'reassignShift',
  ReplaceShift = 'replaceShift',
  ReviewSwapShift = 'reviewSwapShift',
  ShiftHistory = 'shiftHistory',
  SwapShift = 'swapShift',
  UnassignShift = 'unassignShift',
  ViewOpenShiftResponses = 'viewOpenShiftResponses',
  ViewTimesheet = 'viewTimesheet',
  WithdrawOpenShift = 'withdrawOpenShift',
}

interface ShiftActionControllerInterface
  extends Record<ShiftActionControlEnum, ShiftActionControlInterface> {
  actingShift: Shift | null;
}

class ShiftActionController implements ShiftActionControllerInterface {
  actingShift: Shift | null = null;

  [ShiftActionControlEnum.AssignShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) =>
      !isAssigned(shift) && isFuture(shift.startsAt),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.AssignShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.AssignShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.CancelShiftSwap]: ShiftActionControlInterface = {
    isEnabled: ({
      shift,
      loggedInEmployeeId,
      scheduleSettings,
      shiftSwaps,
    }: {
      shift: Shift;
      loggedInEmployeeId: number;
      scheduleSettings: ScheduleSettings;
      shiftSwaps: ShiftSwap[];
    }) =>
      scheduleSettings.shiftSwappingEnabled &&
      canCancelSwapShift(
        shift,
        loggedInEmployeeId,
        shiftSwaps,
        scheduleSettings.swapCutOffHoursBeforeShift,
      ),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.CancelShiftSwap].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.CancelShiftSwap].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ClaimOpenShift]: ShiftActionControlInterface = {
    isEnabled: ({
      shift,
      loggedInEmployeeId,
      openShiftResponses,
      scheduleSettings,
    }: {
      shift: Shift;
      loggedInEmployeeId: number;
      openShiftResponses: OpenShiftResponse[];
      scheduleSettings: ScheduleSettings;
    }) =>
      scheduleSettings.enableOpenShifts &&
      canClaimOpenShift(shift, loggedInEmployeeId, openShiftResponses),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ClaimOpenShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ClaimOpenShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.DeleteShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) => canDeleteShift(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.DeleteShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.DeleteShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.DuplicateShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) => isFuture(shift.startsAt),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.DuplicateShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.DuplicateShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.EditShift]: ShiftActionControlInterface = {
    isEnabled: () => true,
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.EditShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.EditShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.MoveToUnassigned]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) =>
      isFuture(shift.startsAt) && isOpenAndUnassigned(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.MoveToUnassigned].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.MoveToUnassigned].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.MoveToOpen]: ShiftActionControlInterface = {
    isEnabled: ({
      shift,
      scheduleSettings,
    }: {
      shift: Shift;
      scheduleSettings: ScheduleSettings;
    }) =>
      scheduleSettings.enableOpenShifts &&
      isFuture(shift.startsAt) &&
      isUnassignedAndNotOpen(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.MoveToOpen].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.MoveToOpen].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.PublishShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) => canPublishShift(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.PublishShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.PublishShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ReassignShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift, timezone }: { shift: Shift; timezone: string }) =>
      canReassignShift(shift, timezone),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ReassignShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ReassignShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ReplaceShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift, timezone }: { shift: Shift; timezone: string }) =>
      canReplaceShift(shift, timezone),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ReplaceShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ReplaceShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ReviewSwapShift]: ShiftActionControlInterface = {
    isEnabled: ({ shiftSwap }: { shiftSwap: ShiftSwap | null }) =>
      shiftSwap && shiftSwapPendingApproval(shiftSwap),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ReviewSwapShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ReviewSwapShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ShiftHistory]: ShiftActionControlInterface = {
    isEnabled: () => true,
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ShiftHistory].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ShiftHistory].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.SwapShift]: ShiftActionControlInterface = {
    isEnabled: ({
      shift,
      loggedInEmployeeId,
      scheduleSettings,
      shiftSwaps,
    }: {
      shift: Shift;
      loggedInEmployeeId: number;
      scheduleSettings: ScheduleSettings;
      shiftSwaps: ShiftSwap[];
    }) =>
      scheduleSettings.shiftSwappingEnabled &&
      canSwapShift(shift, loggedInEmployeeId, shiftSwaps),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.SwapShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.SwapShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.UnassignShift]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) => canUnassignShift(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.UnassignShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.UnassignShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.ViewOpenShiftResponses]: ShiftActionControlInterface =
    {
      isEnabled: ({
        shift,
        openShiftResponse,
      }: {
        shift: Shift;
        openShiftResponse: OpenShiftResponse | null;
      }) =>
        isFuture(shift.startsAt) &&
        isPublished(shift) &&
        isOpenAndUnassigned(shift) &&
        !!openShiftResponse,
      isOpen: false,
      call: (shift: Shift) => {
        this.actingShift = shift;
        this[ShiftActionControlEnum.ViewOpenShiftResponses].isOpen = true;
      },
      close: (clearShift: boolean = false) => {
        this[ShiftActionControlEnum.ViewOpenShiftResponses].isOpen = false;
        if (clearShift) {
          this.actingShift = null;
        }
      },
    };

  [ShiftActionControlEnum.ViewTimesheet]: ShiftActionControlInterface = {
    isEnabled: ({ shift }: { shift: Shift }) =>
      isLocked(shift) && isAssigned(shift),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.ViewTimesheet].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.ViewTimesheet].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };

  [ShiftActionControlEnum.WithdrawOpenShift]: ShiftActionControlInterface = {
    isEnabled: ({
      shift,
      loggedInEmployeeId,
      openShiftResponses,
    }: {
      shift: Shift;
      loggedInEmployeeId: number;
      openShiftResponses: OpenShiftResponse[];
    }) =>
      canWithdrawOpenShiftRequest(
        shift,
        loggedInEmployeeId,
        openShiftResponses,
      ),
    isOpen: false,
    call: (shift: Shift) => {
      this.actingShift = shift;
      this[ShiftActionControlEnum.WithdrawOpenShift].isOpen = true;
    },
    close: (clearShift: boolean = false) => {
      this[ShiftActionControlEnum.WithdrawOpenShift].isOpen = false;
      if (clearShift) {
        this.actingShift = null;
      }
    },
  };
}

export default new ShiftActionController();
